import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { formatDateTime } from "@/components/admin/ui";
import { ASSESSMENT_STATUS_META } from "./salesMeta";
import { fmtLocal } from "@/components/org/office/officeMeta";

const labelClass = "text-xs font-bold uppercase tracking-[0.12em] text-slate-400";
const inputClass = "mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white";
const btn = "rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2 text-xs font-bold text-white transition-colors duration-150 hover:bg-[#0040CC] disabled:cursor-not-allowed disabled:opacity-50";

export const AssessmentTab = ({ lead, onRefresh, onError, API }) => {
  const [data, setData] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [notes, setNotes] = useState("");
  const [findings, setFindings] = useState("");
  const [showComplete, setShowComplete] = useState(false);
  const [duration, setDuration] = useState("60");
  const [extraWorkerIds, setExtraWorkerIds] = useState([]);
  const [slots, setSlots] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/org/sales/leads/${lead.lead_id}/assessment`, { withCredentials: true })
      .then(({ data: d }) => { setData(d); setNotes(d.assessment.notes || ""); })
      .catch((err) => onError(formatApiErrorDetail(err.response?.data?.detail)));
    axios.get(`${API}/org/sales/leads/${lead.lead_id}/schedule/context`, { withCredentials: true })
      .then(({ data: d }) => setCtx(d)).catch(() => {});
  }, [API, lead.lead_id, onError]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="text-sm text-slate-400" data-testid="assessment-loading">Loading…</p>;
  const { assessment, methods, method_labels, methods_requiring_scheduling, quote_eligible, job } = data;
  const statusMeta = ASSESSMENT_STATUS_META[assessment.status];

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); load(); await onRefresh(); } catch (err) { onError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const setMethod = (m) => run(() => axios.patch(`${API}/org/sales/leads/${lead.lead_id}/assessment`, { method: m }, { withCredentials: true }));
  const saveNotes = () => run(() => axios.patch(`${API}/org/sales/leads/${lead.lead_id}/assessment`, { notes }, { withCredentials: true }));
  const complete = () => run(() => axios.post(`${API}/org/sales/leads/${lead.lead_id}/assessment/complete`, { findings }, { withCredentials: true }));
  const toggleWorker = (id) => setExtraWorkerIds((ids) => ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id]);

  const findAvailability = async () => {
    const minutes = parseInt(duration, 10);
    if (!minutes || minutes <= 0) { onError("Enter a valid duration first."); return; }
    setBusy(true);
    setSlots(null); setSelectedSlot(null);
    try {
      const { data: d } = await axios.post(`${API}/org/sales/leads/${lead.lead_id}/assessment/schedule/availability`, {
        duration_minutes: minutes, start_date: new Date().toISOString().slice(0, 10), days_ahead: 14,
      }, { withCredentials: true });
      setSlots(d.slots);
    } catch (err) { onError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const book = () => run(() => axios.post(`${API}/org/sales/leads/${lead.lead_id}/assessment/schedule/book`, {
    start_at: selectedSlot.start_local, end_at: selectedSlot.end_local, extra_assigned_user_ids: extraWorkerIds, notes: null,
  }, { withCredentials: true }).then(() => { setSlots(null); setSelectedSlot(null); }));

  const needsScheduling = assessment.method && methods_requiring_scheduling.includes(assessment.method);
  const slotsByDate = (slots || []).reduce((acc, s) => { (acc[s.date] = acc[s.date] || []).push(s); return acc; }, {});

  return (
    <div className="space-y-6" data-testid="assessment-tab">
      <div className="flex items-center justify-between">
        <p className={labelClass}>Assessment Method</p>
        <span className={`rounded-full border px-2.5 py-1 text-xs font-bold ${statusMeta.cls}`} data-testid="assessment-status-badge">{statusMeta.label}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {methods.map((m) => (
          <button key={m} type="button" onClick={() => setMethod(m)} disabled={busy} data-testid={`assessment-method-${m}`}
            className={`rounded-lg border px-3 py-1.5 text-xs font-bold ${assessment.method === m ? "border-[#0052FF] bg-[#0052FF] text-white" : "border-slate-200 text-slate-600 dark:border-white/15 dark:text-slate-300"}`}>
            {method_labels[m]}
          </button>
        ))}
      </div>

      {needsScheduling && !job && ctx && (
        <div className="border-t border-slate-200 pt-4 dark:border-white/10" data-testid="assessment-schedule-section">
          <p className={labelClass}>Schedule the Assessment</p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Team: <span className="font-bold text-slate-700 dark:text-slate-200">{ctx.team_name || "Unassigned"}</span> · Lead Owner: <span className="font-bold text-slate-700 dark:text-slate-200">{ctx.lead_owner_name || "Unassigned"}</span>
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Duration (minutes)</label>
              <input type="number" min={15} value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="assessment-duration-input" className={inputClass} />
            </div>
          </div>
          {ctx.additional_workers?.length > 0 && (
            <div className="mt-3">
              <label className={labelClass}>Additional Workers</label>
              <div className="mt-1 max-h-28 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2 dark:border-white/15">
                {ctx.additional_workers.map((u) => (
                  <label key={u.user_id} className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-white/5">
                    <input type="checkbox" checked={extraWorkerIds.includes(u.user_id)} onChange={() => toggleWorker(u.user_id)} data-testid={`assessment-extra-worker-${u.user_id}`} />
                    {u.name}
                  </label>
                ))}
              </div>
            </div>
          )}
          <button type="button" onClick={findAvailability} disabled={busy} data-testid="assessment-find-availability-btn" className={`${btn} mt-3`}>Find Availability</button>
          {slots && (slots.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500 dark:text-slate-400" data-testid="assessment-no-slots">No openings in the next 14 days.</p>
          ) : (
            <div className="mt-3 space-y-2" data-testid="assessment-slots-list">
              {Object.entries(slotsByDate).map(([d, daySlots]) => (
                <div key={d}>
                  <p className="text-xs font-bold text-slate-500 dark:text-slate-400">{new Date(d).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}</p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {daySlots.map((s) => (
                      <button key={s.start_at} type="button" onClick={() => setSelectedSlot(s)} data-testid={`assessment-slot-${s.start_at}`}
                        className={`rounded-lg border px-2.5 py-1.5 text-xs font-bold ${selectedSlot?.start_at === s.start_at ? "border-[#0052FF] bg-[#0052FF] text-white" : "border-slate-200 text-slate-600 dark:border-white/15 dark:text-slate-300"}`}>
                        {fmtLocal(s.start_at).split(", ").slice(1).join(", ")}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
          {selectedSlot && (
            <button type="button" onClick={book} disabled={busy} data-testid="assessment-book-btn" className={`${btn} mt-3`}>Schedule Assessment</button>
          )}
        </div>
      )}

      {job && (
        <div className="border-t border-slate-200 pt-4 dark:border-white/10" data-testid="assessment-job-card">
          <p className={labelClass}>Scheduled Appointment</p>
          <p className="mt-1 text-sm font-bold text-slate-900 dark:text-white">{fmtLocal(job.scheduled_start)}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">{formatDateTime(job.scheduled_start)} → {formatDateTime(job.scheduled_end)}</p>
        </div>
      )}

      <div className="border-t border-slate-200 pt-4 dark:border-white/10">
        <label className={labelClass}>Notes</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="assessment-notes-input" className={inputClass} />
        <button type="button" onClick={saveNotes} disabled={busy} data-testid="assessment-save-notes-btn" className={`${btn} mt-2`}>Save Notes</button>
      </div>

      {assessment.status !== "completed" && assessment.status !== "not_required" && assessment.method && (
        <div className="border-t border-slate-200 pt-4 dark:border-white/10">
          {!showComplete ? (
            <button type="button" onClick={() => setShowComplete(true)} data-testid="assessment-mark-complete-btn" className={btn}>Mark Assessment Complete</button>
          ) : (
            <div data-testid="assessment-complete-form">
              <label className={labelClass}>Findings</label>
              <textarea value={findings} onChange={(e) => setFindings(e.target.value)} rows={3} data-testid="assessment-findings-input" className={inputClass} />
              <button type="button" onClick={complete} disabled={busy} data-testid="assessment-confirm-complete-btn" className={`${btn} mt-2`}>Confirm Complete</button>
            </div>
          )}
        </div>
      )}

      {assessment.findings && <Field label="Findings" value={assessment.findings} testId="assessment-findings-display" />}

      <p className={`text-xs font-bold ${quote_eligible ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`} data-testid="assessment-quote-eligible">
        {quote_eligible ? "✓ Ready to create a Quote" : "Complete the Assessment (or mark it Not Required) before creating a Quote."}
      </p>
    </div>
  );
};

const Field = ({ label, value, testId }) => (
  <div>
    <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">{label}</p>
    <p data-testid={testId} className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">{value}</p>
  </div>
);
